import React, { useCallback, useEffect, useState } from 'react';

const EDIT_FIELDS = [
    { key: 'name', label: 'Name' },
    { key: 'exchange', label: 'Exchange' },
    { key: 'sector', label: 'Sector' },
    { key: 'industry', label: 'Industry' },
    { key: 'currency', label: 'Currency' },
    { key: 'country', label: 'Country' },
];

/** Every row in the stocks table, including symbols on nobody's watchlist. */
const AdminStocks = ({ reloadKey }) => {
    const [stocks, setStocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [editSymbol, setEditSymbol] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');

    const fetchStocks = useCallback(async () => {
        try {
            const r = await fetch('/api/admin/stocks', { credentials: 'include' });
            if (!r.ok) throw new Error('Failed to load stocks');
            setStocks(await r.json());
            setError('');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchStocks(); }, [fetchStocks, reloadKey]);

    const openEditor = (s) => {
        setEditSymbol(s.symbol);
        setEditValues(Object.fromEntries(EDIT_FIELDS.map(f => [f.key, s[f.key]])));
        setEditError('');
    };

    const closeEditor = () => {
        setEditSymbol(null);
        setEditError('');
    };

    const saveEdit = async () => {
        if (!editValues.name.trim()) {
            setEditError('Name cannot be empty.');
            return;
        }
        setEditError('');
        setEditSaving(true);
        try {
            const r = await fetch(`/api/admin/stocks/${encodeURIComponent(editSymbol)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(editValues),
            });
            if (!r.ok) {
                const data = await r.json();
                throw new Error(data.error || 'Update failed');
            }
            closeEditor();
            await fetchStocks();
        } catch (err) {
            setEditError(err.message);
        } finally {
            setEditSaving(false);
        }
    };

    const deleteStock = async (s) => {
        const lists = s.list_count === 1 ? '1 watchlist' : `${s.list_count} watchlists`;
        const impact = s.list_count > 0
            ? `This removes it from ${lists} and deletes its dividends and splits.`
            : 'This deletes its dividends and splits.';
        const confirmed = window.confirm(`Delete ${s.symbol}? ${impact} Stored prices are kept.`);
        if (!confirmed) return;
        setError('');
        try {
            const r = await fetch(`/api/admin/stocks/${encodeURIComponent(s.symbol)}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!r.ok) {
                const data = await r.json();
                throw new Error(data.error || 'Delete failed');
            }
            if (editSymbol === s.symbol) closeEditor();
            await fetchStocks();
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <section className="admin-stock-refresh" aria-labelledby="admin-stocks-heading">
            <h2 id="admin-stocks-heading">All stocks</h2>
            <p className="admin-stock-refresh-intro">
                Every symbol in the <code>stocks</code> table. Symbols on no watchlist are skipped by price
                updates. Delete a delisted symbol to stop it failing.
            </p>
            {error && <div className="alert-error admin-stock-refresh-msg">{error}</div>}
            {loading ? <p>Loading stocks…</p> : (
                <div className="users-table-wrapper">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>Symbol</th>
                                <th>Name</th>
                                <th>Exchange</th>
                                <th>Watchlists</th>
                                <th>Last quote</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stocks.map(s => (
                                <React.Fragment key={s.symbol}>
                                    <tr>
                                        <td><code>{s.symbol}</code></td>
                                        <td>{s.name}</td>
                                        <td>{s.exchange}</td>
                                        <td>
                                            <span className={`status-badge ${s.list_count > 0 ? 'user' : 'pending'}`}>
                                                {s.list_count > 0 ? s.list_count : 'None'}
                                            </span>
                                        </td>
                                        <td>{s.last_quote_date || '—'}</td>
                                        <td>
                                            <div className="actions">
                                                <button
                                                    type="button"
                                                    className="action-btn promote"
                                                    onClick={() => (
                                                        editSymbol === s.symbol ? closeEditor() : openEditor(s)
                                                    )}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className="action-btn deny"
                                                    onClick={() => deleteStock(s)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {editSymbol === s.symbol && (
                                        <tr className="password-editor-row">
                                            <td colSpan="6">
                                                <form
                                                    className="password-editor"
                                                    onSubmit={e => { e.preventDefault(); saveEdit(); }}
                                                >
                                                    {EDIT_FIELDS.map(f => (
                                                        <label key={f.key} className="password-editor-label">
                                                            {f.label}
                                                            <input
                                                                type="text"
                                                                autoComplete="off"
                                                                value={editValues[f.key]}
                                                                onChange={e => setEditValues({
                                                                    ...editValues,
                                                                    [f.key]: e.target.value,
                                                                })}
                                                                disabled={editSaving}
                                                            />
                                                        </label>
                                                    ))}
                                                    <button
                                                        type="submit"
                                                        className="action-btn approve"
                                                        disabled={editSaving}
                                                    >
                                                        {editSaving ? 'Saving…' : 'Save'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn cancel"
                                                        onClick={closeEditor}
                                                        disabled={editSaving}
                                                    >
                                                        Cancel
                                                    </button>
                                                    {editError && (
                                                        <span className="password-editor-error">{editError}</span>
                                                    )}
                                                </form>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {stocks.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="no-users">No stocks found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default AdminStocks;
